import { Color3, type Mesh, MeshBuilder, StandardMaterial } from "@babylonjs/core";
import { BARRIERS, Btn, HeroId, Slot, hv, pk } from "@rivalwatch/shared";
import * as loops from "../../audio/cues/loops.ts";
import type { Cue } from "../../audio/engine.ts";
import { bell, brass, clinks, fm, noise, thump, trim, whoosh } from "../../audio/synth.ts";
import type { ClientHero } from "../types.ts";

const V = { curtainUp: 0, curtainHp: 1, curtainRegenDelay: 2, rings: 3, meteorChannel: 4 } as const;
const PK = { Orb: pk(HeroId.Magnetron, 0), Cannon: pk(HeroId.Magnetron, 1), Meteor: pk(HeroId.Magnetron, 2) } as const;

const ironVolley: Cue = trim(11, (o) => {
  fm(o, { f: 210, ratio: 1.6, index: 5, index1: 0.4, gain: 0.18, d: 0.22 });
  noise(o, { color: "pink", type: "bandpass", f: 900, f1: 2500, gain: 0.23, d: 0.16 });
  return 0.42;
});
const ringCannon: Cue = trim(1, (o) => {
  thump(o, 0.55, 95, 42, 0.18);
  fm(o, { f: 130, ratio: 2.5, index: 8, index1: 0, gain: 0.22, d: 0.32 });
  whoosh(o, 0.28, 400, 1400, 0.18);
  return 0.55;
});
const magneticShield: Cue = trim(15, (o) => {
  bell(o, 330, 0.25, 0.4);
  noise(o, { color: "white", type: "bandpass", f: 1800, f1: 500, gain: 0.18, d: 0.35 });
  return 0.7;
});
const magneticReload = (seconds: number): Cue => trim(5, (o) => {
  clinks(o, 5, 0.08, 0.26, 900, 2600);
  fm(o, { f: 180, ratio: 2.1, index: 3, index1: 0, gain: 0.08, d: seconds * 0.5, delay: seconds * 0.35 });
  noise(o, { color: "pink", type: "bandpass", f: 600, f1: 1400, gain: 0.1, d: seconds * 0.3, delay: seconds * 0.5 });
  return seconds + 0.15;
});

const theme = {
  bpm: 86,
  octave: -1,
  voice: brass,
  notes: [
    [0, 1],
    [6, 1],
    [12, 1.5],
    [11, 0.5],
    [6, 2],
  ],
} satisfies ClientHero["audio"]["theme"];

type Gear = { rings: Mesh[]; cannon: Mesh };
const mat = (scene: Parameters<NonNullable<ClientHero["projectiles"]>[number]>[0], hex: string, alpha = 1) => {
  const m = new StandardMaterial("magnetron-mat", scene);
  m.diffuseColor = Color3.FromHexString(hex);
  m.emissiveColor = Color3.FromHexString(hex).scale(0.35);
  m.alpha = alpha;
  return m;
};

const hero: ClientHero<Gear> = {
  id: HeroId.Magnetron,
  bulk: 1.25,
  shape: { head: 1, torso: 1.18, arms: 1.15 },
  gear(g) {
    const metal = g.toon("#6e738a");
    const red = g.toon("#8d1f35");
    const glow = g.glow("#ff8aa0", 0.85);
    const cape = g.add(MeshBuilder.CreateBox("mag-cape", { width: 1.05, height: 1.15, depth: 0.06 }, g.scene), g.rig.torso, red);
    cape.position.set(0, -0.22, 0.38);
    const collar = g.add(MeshBuilder.CreateBox("mag-collar", { width: 0.9, height: 0.2, depth: 0.08 }, g.scene), g.rig.torso, metal);
    collar.position.set(0, 0.25, -0.36);
    const helm = g.add(MeshBuilder.CreateCylinder("mag-helm", { diameter: 0.6, height: 0.32, tessellation: 14 }, g.scene), g.rig.head, metal);
    helm.position.y = 0.16;
    for (const side of [-1, 1]) {
      const fin = g.add(MeshBuilder.CreateBox("mag-fin", { width: 0.08, height: 0.46, depth: 0.18 }, g.scene), g.rig.head, metal);
      fin.position.set(side * 0.35, 0.2, 0);
      fin.rotation.z = side * 0.35;
    }
    const rings: Mesh[] = [];
    for (let i = 0; i < 4; i++) {
      const ring = g.add(MeshBuilder.CreateTorus("back-ring", { diameter: 0.48 + i * 0.08, thickness: 0.025, tessellation: 24 }, g.scene), g.rig.torso, glow);
      ring.position.set((i - 1.5) * 0.16, 0.12, 0.48);
      ring.rotation.x = Math.PI / 2;
      rings.push(ring);
    }
    const cannon = g.add(MeshBuilder.CreateCylinder("mag-cannon", { diameter: 0.22, height: 0.78, tessellation: 12 }, g.scene), g.rig.handR, metal);
    cannon.rotation.x = Math.PI / 2;
    cannon.position.z = -0.38;
    return { rings, cannon };
  },
  animate(gear, f) {
    const channeling = f.v[V.meteorChannel] > 0;
    const lit = channeling ? 4 : Math.floor(f.v[V.rings] + 0.01);
    gear.rings.forEach((r, i) => {
      r.setEnabled(i < Math.max(1, lit));
      const orbit = f.t * (1.2 + i * 0.35) + i * Math.PI * 0.5;
      r.position.x = Math.cos(orbit) * (0.3 + i * 0.03);
      r.position.y = 0.08 + Math.sin(orbit * 1.7) * 0.08;
      r.position.z = 0.5 + Math.sin(orbit) * 0.08;
      r.rotation.z += f.dt * (2.5 + i);
    });
    gear.cannon.scaling.setAll(1 + (channeling ? 0.28 : f.action === 2 ? 0.22 : 0));
    if (channeling || f.v[V.curtainUp] > 0 || f.action === 4) return "holding-both";
    return f.action === 1 || f.action === 2 ? "holding-right-shoot" : undefined;
  },
  hud(c, eid) {
    return [
      { label: "Curtain", value: hv(c, eid, V.curtainHp) / BARRIERS[HeroId.Magnetron]!.maxHp, color: "#d6d8ff" },
      { label: "Rings", value: hv(c, eid, V.rings) / 3, color: "#ff8aa0" },
    ];
  },
  lifting(c, eid, buttons, grounded) {
    return !grounded && !!(buttons & Btn.Jump);
  },
  projectiles: Object.fromEntries([PK.Orb, PK.Cannon, PK.Meteor].map((kind) => [kind, (scene) => {
    const size = kind === PK.Meteor ? 1.2 : kind === PK.Cannon ? 0.5 : 0.32;
    const mesh = MeshBuilder.CreateSphere("mag-shot", { diameter: size, segments: 10 }, scene);
    mesh.material = mat(scene, kind === PK.Meteor ? "#ff4e67" : kind === PK.Cannon ? "#ff8aa0" : "#d6d8ff", 0.9);
    return {
      gear: { mesh },
      update({ mesh }, f) {
        mesh.position.set(f.pos.x, f.pos.y, f.pos.z);
        mesh.rotation.y += f.dt * 8;
      },
      dispose: () => mesh.dispose(),
    };
  }])) as ClientHero["projectiles"],
  icons: {
    [Slot.Primary]: "M4 12c4-5 12-5 16 0M4 12c4 5 12 5 16 0M8 12h8",
    [Slot.Secondary]: "M5 12h14M12 5v14M7 7l10 10M17 7 7 17",
    [Slot.Ability1]: "M4 18V8l8-4 8 4v10M8 12h8",
    [Slot.Ability2]: "M5 16c3-8 11-8 14 0M7 19h10M12 5v9",
    [Slot.Ability3]: "M12 4a8 8 0 1 0 0 16a8 8 0 0 0 0-16M12 8v8",
    [Slot.Ultimate]: "M12 3l3 6 6 1-4.5 4 1 7-5.5-3.5L6 21l1-7-4.5-4 6-1z",
    passive: "M4 12a8 8 0 0 1 16 0M7 7c3 3 7 3 10 0M7 17c3-3 7-3 10 0",
  },
  audio: {
    weight: "heavy",
    fire: (ctx) => ({ key: ctx?.alt ? "fire:magnetron:cannon" : "fire:magnetron:volley", cue: ctx?.alt ? ringCannon : ironVolley }),
    reload: magneticReload,
    ability: { [Slot.Ability1]: magneticShield, [Slot.Ability2]: magneticShield, [Slot.Ability3]: magneticShield, [Slot.Ultimate]: ringCannon },
    theme,
    themeTrim: { heroic: -1, hostile: -6.5, tease: -1 },
    loops: ({ v, alive }) => (alive && v[V.curtainUp] > 0 ? [{ key: "mag-curtain", build: loops.ward, gain: 0.75, value: v[V.curtainHp] / BARRIERS[HeroId.Magnetron]!.maxHp }] : []),
    flight: { [PK.Orb]: loops.flight("orb"), [PK.Cannon]: loops.flight("rocket"), [PK.Meteor]: loops.flight("rocket") },
  },
};

export default hero;
